import React, { createContext, useContext, useState } from "react";
import axios from "axios";
import useLocalStorage from "hooks/useLocalStorage";
import {useUI} from "../ui/uiContext";

/**
 *
 * @param selectedOptions
 */
function transformToAPIFormat(selectedOptions) {
  return {
    selectedInspectionOptions: Object.keys(selectedOptions).map(key => {
      const option = selectedOptions[key];
      const transformedOption = {
        name: key,
        rating: option.rating,
        comments: option.comments,
        inspectionSubQueries: []
      };

      if (option.inspectionSubQueries) {
        transformedOption.inspectionSubQueries.push({
          name: option.inspectionSubQueries.name,
          selectType: option.inspectionSubQueries.selectType,
          ratings: option.inspectionSubQueries.ratings
        });
      }

      if (option.inspectionSubQueriesText) {
        transformedOption.inspectionSubQueries.push({
          name: option.inspectionSubQueriesText.name,
          selectType: "Open text",
          ratings: option.inspectionSubQueriesText.ratings
        });
      }

      return transformedOption;
    })
  };
}

/**
 * DealerContext is a React context that provides dealer-specific data and functionalities.
 * It allows components to consume and act on dealer-related information such as inspection data.
 */
const CustomerContext = createContext();

/**
 * Custom hook to access the DealerContext.
 * @returns {object} The DealerContext with inspection data and related functionalities.
 */
export const useCustomer = () => useContext(CustomerContext);

/**
 * DealerProvider is a context provider component that manages dealer-specific state,
 * including inspection data and API interactions.
 * @param {object} props - Props for the DealerProvider component.
 * @param {React.ReactNode} props.children - Child components that will have access to DealerContext.
 * @returns {React.ReactElement} A context provider component wrapping its children.
 */
export const CustomerProvider = ({ children }) => {
  const [zip, setZip] = useState(null);
  const {showAlert, showLoadingSpinner, hideLoadingSpinner} = useUI();
  const [rangeInMiles, setRangeInMiles] = useState(25);
  const [dealerList, setDealerList] = useState(null);
  const [customerDetails, setCustomerDetails] = useLocalStorage("customerDetails", "");
  const [pickupCharge, setPickupCharge] = useLocalStorage("pickupCharge", "");
  const [sessionToken, setSessionToken] = useLocalStorage("sessionToken", "");
  const [inspectionID, setInspectionID] = useLocalStorage("inspectionID", "");
  const [loanID, setLoanID] = useLocalStorage("loadID", "");
  const [inspection, setInspection] = useLocalStorage("inspection", "");
  const [charges, setCharges] = useLocalStorage("ewt_charges", "");
  const [selectedItemCharges, setSelectedItemCharges] = useLocalStorage("selectedItemCharges", "");
  const [deliveryOption, setDeliveryOption] = useLocalStorage("pickup_or_dropOff", "dropOff");

  const [images, setImages] = useState([]);

  const axiosInstance = axios.create({
    baseURL: window?.globalConfig.rvlApiUrl
  });

  /**
   * Confirms the identity of a customer by validating their account number and the last 6 characters of their VIN.
   * @param {object} params An object containing the account number and the last 6 characters of the VIN.
   * @param {string} params.accountNumber The customer's account number, expected to be numeric.
   * @param {string} params.last6VIN The last 6 characters of the vehicle's VIN, expected to be numeric.
   * @returns {Promise<object>} An object indicating the success status and any error messages.
   */
  const confirmIdentity = async ({ accountNumber, last6VIN }) => {
    // Validate the account number and last 6 VIN characters
    const accountNumberPattern = /^\d+$/; // Adjust regex based on the expected format
    const last6VINPattern = /^\d{6}$/; // Assuming the last 6 characters are numeric

    if (!accountNumberPattern.test(accountNumber)) {
      console.error("Invalid account number format.");
      showAlert("Invalid account number format", "error");
      return { success: false, error: "Invalid account number format." };
    }

    if (!last6VINPattern.test(last6VIN)) {
      console.error("Invalid VIN format.");
      showAlert("Invalid VIN format", "error");
      return { success: false, error: "Invalid VIN format." };
    }

    showLoadingSpinner();

    try {
      const response = await axiosInstance.post("/api/Customer/ConfirmIdentity", { accountNumber, last6VIN });
      const { success, sessionToken, loanID, message } = response.data;
      hideLoadingSpinner();
      if (success && sessionToken && loanID) {
        setSessionToken(sessionToken);
        setLoanID(loanID);

        return {
          success,
          error: null
        };
      }

      return {
        success: false,
        error: message
      };

    } catch (error) {
      console.log(error);
      hideLoadingSpinner();
      showAlert(error.message, "error");
      return {
        success: false,
        error: error.message // Providing error details for better debugging
      };
    }
  };

  const fetchCustomerDetails = async () => {
    try {
      showLoadingSpinner();
      const response = await axiosInstance.post("/api/Customer/Details", {loanID, sessionToken});
      const {customerDetails, success, message} = response.data;
      console.log(customerDetails);

      if (!success) {
        showAlert(message, "error");
        hideLoadingSpinner();
        return {success};
      }

      setCustomerDetails(customerDetails);
      hideLoadingSpinner();
      return {success};
    }
    catch (error) {
      console.log(error);
      hideLoadingSpinner();
      showAlert(error.message, "error");
      return {success: false};
    }
  };

  const fetchDealersByZip = async () => {
    if (!zip || !rangeInMiles) {
      alert("ZIP and range required");
      return false;
    }

    try {
      const response = await axiosInstance.post("/api/Customer/DealerList", {zip, rangeInMiles, sessionToken});
      console.log(response);
      const {dealers, success} = response.data || {};
      console.log(response.data);
      if (success) {
        setDealerList(dealers);
      }
    }
    catch (error) {
      console.log(error);
      showAlert(error.message, "error");
    }
  };

  const fetchCRMaster = async (vehicleModelID) => {
    try {
      const response = await axiosInstance.post("/api/GetInspectionMaster", {vehicleModelID, sessionToken});
      console.log(response.data);
      setInspection(response.data);
    }

    catch (error) {
      console.log(error);
      showAlert(error.message, "error");
    }
  };

  const submitConditionReport = async ({conditionResult, mileage}) => {
    try {
      try {
        const {vehicleID, vehicleModelID} = customerDetails;
        const transform = transformToAPIFormat(conditionResult);
        const transformedImages = Object.entries(images).map(([name, { url }]) => ({
          name,
          url,
        }));

        const payload = {
          "sessionToken": sessionToken,
          "vehicleID": vehicleID,
          "vehicleModelID": vehicleModelID,
          "milesHours": mileage,
          ...transform,
          "inspectionPictures": transformedImages
        };

        console.log("**** Submitting CR payload ****");
        console.log(payload);

        const response = await axiosInstance.post("/api/Inspection/Create", payload);
        const {success, inspectionID, message, returnEligible} = response.data;

        console.log(`*** Successful: ${success} ***`);

        setInspectionID(inspectionID);
        setImages([]);
        return {
          success,
          inspectionID,
          returnEligible,
          message
        };
      }
      catch (error) {
        console.log(error);
        showAlert(error.message, "error");
        return {
          success: false,
        };
      }

    }
    catch (error) {
      showAlert(error.message, "error");
    }
  };

  const submitPickupDetails = async (payload) => {
    try {

      ///api/Customer/VehicleDeliveryLocation
      const response = await axiosInstance.post("api/Customer/VehicleDeliveryLocation", {...payload, sessionToken});

      if (!response.data) {
        return {
          success: false,
          message: "There was a problem submitting your pickup request"
        };
      }

      const {success, message} = response.data;

      return {
        success,
        message
      };
    }
    catch (error) {
      return {
        success: false,
        message: error.message
      };
    }

  };

  const getZipFromCoords = async (coords) => {
    try {
      const {latitude, longitude} = coords;
      const response = await axiosInstance.post("api/Customer/GetZipCodeService", {
        sessionToken,
        latitude,
        longitude
      });

      console.log(response);
      return response.data;
    }
    catch (error) {
      console.log(error);
    }
  };

  const fetchTotalCharges = async () => {
    const {vehicleID} = customerDetails;

    if (!vehicleID || !inspectionID || !sessionToken) {
      console.log("vehicleID: ", vehicleID);
      console.log("inspectionID: ", inspectionID);
      console.log("sessionToken: ", sessionToken);
      showAlert("There was a problem calculating your charges. Missing vehicle, inspection or session", "error");
      return;
    }

    showLoadingSpinner();
    try {
      const response = await axiosInstance.post("/api/Inspection/GetInspectionItemCharges", {vehicleID, inspectionID, sessionToken});

      const {success, message, pickupCharge, selectedItemCharges, totalSelectedItemCharge, dispositionCharge, estimatedMileageCharge, totalActualChargeIncludingPickup  } = response.data;

      if (success) {
        setCharges({
          pickupCharge,
          selectedItemCharges,
          totalSelectedItemCharge,
          dispositionCharge,
          estimatedMileageCharge,
          totalActualChargeIncludingPickup
        });
      }

      if (!success) {
        showAlert(`There was a problem calculating your charges: ${message}`, "error");
      }

      hideLoadingSpinner();
      return {
        success,
        message
      };

    }
    catch (error) {
      console.log(error);
      showAlert(`There was a problem calculating your charges: ${error.message}`, "error");
      hideLoadingSpinner();
      return {
        success: false,
        message: error.message
      };
    }
  };

  /**
   * Adds a new image to the existing list of images.
   * @param {object} image - The image object to be added.
   * @param {string} image.name - The name of the image.
   * @param {string} image.pictureID - The unique identifier for the image.
   * @param {string} image.url - The URL of the image.
   * @returns {void}
   */
  const addImage = ({ name, pictureID, url }) => {
    setImages((prevImages) => ({
      ...prevImages,
      [name]: {
        pictureID,
        url,
      },
    }));
  };

  const removeImage = (name) => {
    setImages((prevImages) => {
      const updatedImages = { ...prevImages };
      delete updatedImages[name]; // Remove the specified image
      return updatedImages;
    });
  };

  const submitPOADetails = async (poa) => {
    try {
      const {vehicleID} = customerDetails;
      await axiosInstance.post("/api/POAConfirmation", {
        sessionToken,
        "vehicleID": vehicleID,
        "poaAccepted": true,
        ...poa
      });
    }
    catch (error) {
      console.log(error);
    }
  };

  const EWTChargesDecision = async ({ewtChargesAccepted, ewtCharges, poaSigner, customerName}) => {
    showLoadingSpinner();
    try {
      const payload = {
        sessionToken,
        inspectionID,
        ewtChargesAccepted: ewtChargesAccepted,
        ewtCharges,
        // poaSigner,
        customerName: customerDetails.customerName,
        comment: "No comments"
      };

      const response = await axiosInstance.post("/api/Inspection/EWTChargesDecision", payload);

      const {success} = response.data;

      hideLoadingSpinner();
      return {
        success,
      };
    }
    catch (error) {
      hideLoadingSpinner();
      return {
        success: false,
      };
    }
  };

  const value = {
    fetchDealersByZip,
    setRangeInMiles,
    rangeInMiles,
    setZip,
    zip,
    dealerList,
    getZipFromCoords,
    confirmIdentity,
    sessionToken,
    fetchCustomerDetails,
    customerDetails,
    submitConditionReport,
    fetchCRMaster,
    selectedItemCharges,
    submitPickupDetails,
    loanID,
    pickupCharge,
    addImage,
    removeImage,
    images,
    deliveryOption, setDeliveryOption,
    inspection,
    charges,
    fetchTotalCharges,
    submitPOADetails,
    EWTChargesDecision
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};
